<script setup>
// Paywall dos módulos de produto: abre quando qualquer API devolve 402
// code=module_required (interceptor em APIHelper emite o evento). CTA leva
// pra Assinatura com o módulo já no carrinho (?module=<key>).
//
// Preço: cada módulo pode custar um valor diferente (Meet/Campanhas/Marketing
// R$47/mês, Ligações R$197/mês — 2026-08, valor único por conta). O preço vem
// do catálogo (BillingAPI.getAddons) pra nunca ficar diferente do que o
// carrinho de Assinatura (PlanResourcesCard) e o backend de fato cobram.
// `fallbackPrice` só é usado se a chamada ao catálogo falhar (ex.: sem rede).
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import BillingAPI from 'dashboard/api/billing';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const MODULE_META = {
  meet: {
    icon: 'i-lucide-video',
    bullets: 3,
    slug: 'modulo-meet',
    fallbackPrice: 47,
  },
  campaigns: {
    icon: 'i-lucide-megaphone',
    bullets: 3,
    slug: 'modulo-campanhas',
    fallbackPrice: 47,
  },
  marketing: {
    icon: 'i-lucide-line-chart',
    bullets: 3,
    slug: 'modulo-marketing',
    fallbackPrice: 47,
  },
  calling: {
    icon: 'i-lucide-phone',
    bullets: 3,
    slug: 'modulo-ligacoes',
    fallbackPrice: 47,
    // Único módulo com custo de uso além da mensalidade: o minuto falado pela
    // IA é cobrado à parte (Calling::CallLogRecorder#charge_ai_voice_minutes,
    // R$1/min). Sem dizer isso aqui, "a IA atende por voz" é lido como incluso.
    hasNote: true,
  },
  // Chave vem de EnsuresProductModule#ensure_product_module! (`module_key`),
  // por isso é 'social_studio' e não 'social' como os outros — não pode
  // divergir do que o 402 manda ou o paywall nunca abre.
  social_studio: {
    icon: 'i-lucide-share-2',
    bullets: 3,
    slug: 'modulo-social',
    fallbackPrice: 47,
  },
  ai_studio: {
    icon: 'i-lucide-brain',
    bullets: 3,
    slug: 'modulo-ia',
    fallbackPrice: 97,
    hasNote: true,
  },
  // Módulos próprios desde 26/08 (saíram de Captação/Estúdio Social). Chave
  // ausente aqui = paywall que NUNCA abre (o show() ignora módulo
  // desconhecido) — todo módulo novo do ModuleAccess precisa entrar no mapa.
  forms: {
    icon: 'i-lucide-file-text',
    bullets: 3,
    slug: 'modulo-formularios',
    fallbackPrice: 47,
  },
  comment_automations: {
    icon: 'i-lucide-message-square-reply',
    bullets: 3,
    slug: 'modulo-comentarios',
    fallbackPrice: 47,
  },
  pages: {
    icon: 'i-lucide-panels-top-left',
    bullets: 3,
    slug: 'modulo-paginas',
    fallbackPrice: 97,
  },
};

const isOpen = ref(false);
const moduleKey = ref('meet');
// Bloqueante (gate de tela): fechar sem ativar volta pra tela anterior.
const isBlocking = ref(false);
const addonsCatalog = ref([]);
// Evita refetch a cada abertura; a busca só roda em show() (ver comentário
// abaixo sobre por que não pode ser no onMounted).
const catalogLoaded = ref(false);

const meta = computed(() => MODULE_META[moduleKey.value] || MODULE_META.meet);

const bulletKeys = computed(() =>
  Array.from(
    { length: meta.value.bullets },
    (_, i) => `BILLING.PAYWALL.${moduleKey.value.toUpperCase()}_BULLET_${i + 1}`
  )
);

const brl = v =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    v || 0
  );

const price = computed(() => {
  const addon = addonsCatalog.value.find(a => a.slug === meta.value.slug);
  return brl(addon?.monthly_price ?? meta.value.fallbackPrice);
});

const fetchAddonsCatalog = async () => {
  if (catalogLoaded.value) return;
  try {
    const { data } = await BillingAPI.getAddons();
    addonsCatalog.value = data.addons || [];
    catalogLoaded.value = true;
  } catch {
    // Sem catálogo, o preço cai no fallbackPrice; não trava o paywall.
    addonsCatalog.value = [];
  }
};

const show = payload => {
  if (!MODULE_META[payload?.module]) return;
  moduleKey.value = payload.module;
  isBlocking.value = payload.blocking === true;
  isOpen.value = true;
  // Busca o catálogo só quando o paywall realmente abre: aqui a rota
  // sempre tem accountId, diferente do onMounted (montado na raiz do App,
  // roda até na tela de login e derrubava um FATAL RoutingError no log).
  fetchAddonsCatalog();
};

const close = () => {
  isOpen.value = false;
  if (isBlocking.value) {
    isBlocking.value = false;
    router.back();
  }
};

const goToBilling = () => {
  isBlocking.value = false;
  close();
  router.push({
    name: 'billing_settings_index',
    params: { accountId: route.params.accountId },
    query: { module: moduleKey.value },
  });
};

onMounted(() => {
  emitter.on('showModulePaywall', show);
});
onBeforeUnmount(() => emitter.off('showModulePaywall', show));
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      @click.self="close"
    >
      <div
        class="w-full max-w-md rounded-2xl p-[1.5px] bg-gradient-to-br from-n-brand via-n-iris-9 to-n-teal-9 shadow-xl"
      >
        <div
          class="rounded-[14px] bg-n-solid-1 p-7 flex flex-col items-center text-center"
        >
          <div
            class="w-14 h-14 rounded-2xl bg-gradient-to-br from-n-brand to-n-iris-9 flex items-center justify-center mb-4 shadow-lg"
          >
            <span :class="meta.icon" class="w-7 h-7 text-white" />
          </div>

          <span
            class="text-[11px] font-semibold uppercase tracking-widest text-n-brand mb-1"
          >
            {{ t('BILLING.PAYWALL.EYEBROW') }}
          </span>
          <h2 class="text-xl font-semibold text-n-slate-12 mb-1.5">
            {{ t(`BILLING.PAYWALL.${moduleKey.toUpperCase()}_TITLE`) }}
          </h2>
          <p class="text-sm text-n-slate-11 mb-5">
            {{ t(`BILLING.PAYWALL.${moduleKey.toUpperCase()}_DESC`) }}
          </p>

          <ul class="w-full flex flex-col gap-2 mb-6 text-left">
            <li
              v-for="key in bulletKeys"
              :key="key"
              class="flex items-start gap-2.5 text-sm text-n-slate-12"
            >
              <span
                class="i-lucide-check-circle-2 w-4 h-4 text-n-teal-11 shrink-0 mt-0.5"
              />
              {{ t(key) }}
            </li>
          </ul>

          <div class="flex items-baseline gap-1 mb-2">
            <span class="text-3xl font-bold text-n-slate-12 tabular-nums">
              {{ price }}
            </span>
            <span class="text-sm text-n-slate-10">
              {{ t('BILLING.PAYWALL.PER_MONTH') }}
            </span>
          </div>

          <p
            v-if="meta.hasNote"
            data-testid="paywall-note"
            class="text-xs text-n-slate-10 mb-4"
          >
            {{ t(`BILLING.PAYWALL.${moduleKey.toUpperCase()}_NOTE`) }}
          </p>
          <div v-else class="mb-3" />

          <button
            type="button"
            class="w-full py-3 rounded-xl bg-n-brand text-white text-sm font-semibold hover:opacity-90 transition-opacity"
            @click="goToBilling"
          >
            {{ t('BILLING.PAYWALL.CTA') }}
          </button>
          <button
            type="button"
            class="mt-2 py-2 text-xs text-n-slate-10 hover:text-n-slate-11"
            @click="close"
          >
            {{ t('BILLING.PAYWALL.DISMISS') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
